// OWNED BY the broadcast implementation agent (see ../BroadcastTab.tsx ownership note).
//
// One send record row (mock s27): leading attachment thumbnail / channel icon,
// message first line + meta line, truthful "✓ נשלחה" status chip, and an
// expandable body with the full message (whitespace preserved, dir="auto" —
// ported from the legacy BulkMessagingLog expanded row) plus a real attachment
// preview (image inline, video player, file chip with customFileName).

import { useState } from "react";
import { Box, Chip, Collapse, Divider, IconButton, Stack, Typography } from "@material-ui/core";
import {
  AttachFile,
  ImageNotSupported,
  KeyboardArrowDown,
  KeyboardArrowUp,
  PlayCircleOutline,
  WhatsApp
} from "@material-ui/icons";
import Label from "components/Label";
import { formatSentWhen, getAttachmentKind, getMessageFirstLine } from "./broadcastData";
import type { BulkMessageSentMessage } from "../../../../../../@types/sharedSchema";

type BroadcastMessageRowProps = {
  message: BulkMessageSentMessage;
  isLast: boolean;
};

const THUMB_SIZE = 40;

export function BroadcastMessageRow({ message, isLast }: BroadcastMessageRowProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  // The legacy row always tried rendering mediaLink as an image with a
  // broken-image fallback — keep that behavior for unknown extensions.
  const [thumbBroken, setThumbBroken] = useState(false);

  const attachmentKind = getAttachmentKind(message);
  const showImageThumb = attachmentKind === "image" && !thumbBroken;

  const firstLine = getMessageFirstLine(message);
  const when = formatSentWhen(message);

  const metaParts = [when, "וואטסאפ"];
  if (attachmentKind === "image") metaParts.push("🖼 תמונה מצורפת");
  if (attachmentKind === "video") metaParts.push("סרטון מצורף");
  if (attachmentKind === "file") metaParts.push(message.customFileName || "קובץ מצורף");
  if (message.listingId) metaParts.push("מקושר לנכס");

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          py: 1.5,
          px: 0.5,
          cursor: "pointer",
          borderRadius: 1,
          "&:hover": { bgcolor: "action.hover" }
        }}
      >
        {/* Leading visual: image thumbnail / attachment icon / channel icon */}
        {showImageThumb ? (
          <Box
            component="img"
            src={message.mediaLink}
            alt={message.customFileName || "תמונה מצורפת"}
            onError={() => setThumbBroken(true)}
            sx={{
              width: THUMB_SIZE,
              height: THUMB_SIZE,
              flexShrink: 0,
              borderRadius: 1,
              objectFit: "cover",
              bgcolor: "grey.200"
            }}
          />
        ) : (
          <Box
            sx={{
              width: THUMB_SIZE,
              height: THUMB_SIZE,
              flexShrink: 0,
              borderRadius: attachmentKind === "none" ? "50%" : 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor:
                attachmentKind === "none" ? "rgba(84, 214, 44, 0.16)" : "grey.200",
              color: attachmentKind === "none" ? "success.dark" : "text.secondary"
            }}
          >
            {attachmentKind === "none" ? (
              <WhatsApp fontSize="small" />
            ) : attachmentKind === "video" ? (
              <PlayCircleOutline fontSize="small" />
            ) : attachmentKind === "image" ? (
              <ImageNotSupported fontSize="small" />
            ) : (
              <AttachFile fontSize="small" />
            )}
          </Box>
        )}

        {/* Message first line + meta */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap dir="auto">
            {firstLine || "(הודעה ללא טקסט)"}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {metaParts.filter(Boolean).join(" · ")}
          </Typography>
        </Box>

        {/* Truthful status: only successful sends are ever persisted */}
        <Label color="success" sx={{ flexShrink: 0 }}>
          ✓ נשלחה
        </Label>
        <IconButton size="small" sx={{ flexShrink: 0 }}>
          {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </IconButton>
      </Stack>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ pb: 2, pl: `${THUMB_SIZE + 12}px`, pr: 0.5 }}>
          <Typography variant="body2" dir="auto" sx={{ whiteSpace: "pre-wrap" }}>
            {message.message}
          </Typography>

          {message.mediaLink && (
            <Box sx={{ mt: 1.5 }}>
              {attachmentKind === "image" && !thumbBroken ? (
                <Box
                  component="a"
                  href={message.mediaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event: React.MouseEvent) => event.stopPropagation()}
                  sx={{ display: "inline-block", lineHeight: 0 }}
                >
                  <Box
                    component="img"
                    src={message.mediaLink}
                    alt={message.customFileName || "תמונה מצורפת"}
                    onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                      // Same graceful degradation as the legacy expanded row.
                      event.currentTarget.style.display = "none";
                    }}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: 280,
                      borderRadius: 1,
                      objectFit: "contain"
                    }}
                  />
                </Box>
              ) : attachmentKind === "video" ? (
                <Box
                  component="video"
                  src={message.mediaLink}
                  controls
                  sx={{ maxWidth: "100%", maxHeight: 280, borderRadius: 1 }}
                />
              ) : (
                <Chip
                  clickable
                  component="a"
                  href={message.mediaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event: React.MouseEvent) => event.stopPropagation()}
                  icon={<AttachFile />}
                  label={message.customFileName || "קובץ מצורף"}
                  variant="outlined"
                  size="small"
                />
              )}
              {attachmentKind === "image" && message.customFileName && (
                <Typography variant="caption" color="text.secondary" component="div" dir="auto">
                  {message.customFileName}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Collapse>

      {!isLast && <Divider />}
    </>
  );
}
